import { getCanonicalRobotName } from './robotNaming';
import techNormalizer from './technicianNormalizer';

export function isAnalyticsActiveRobot(robot = {}) {
  const lifecycle = [
    robot.lifecycleStatus,
    robot.serviceStatus,
    robot.status,
    robot.state,
  ].filter(Boolean).join(' ').toLowerCase();
  return !/(archived|archive|out[- _]?of[- _]?service|replaced|vervangen|buiten dienst)/.test(lifecycle);
}

export function getRobotIdentityKey(robot = {}){
  const k = robot.kNummer || robot.kNumber || '';
  if (k && String(k).trim()) return `k:${String(k).trim()}`;
  const docId = robot.robotDocumentId || robot.docId || robot.id || '';
  if (docId) return `doc:${docId}`;
  return `name:${(robot.naam||robot.name||'').toString().trim()}`;
}

export function computeAnalyticsCounts({ records = null, manualDocs = null, autoMeldingenCount = 0, linkedIds = null, manualReviewIds = null, robots = [] } = {}) {
  // If `records` provided, derive linked/manualReview from fields
  let manualList = [];
  let autoCount = Number(autoMeldingenCount) || 0;
  let linked = [];
  let manualReview = [];

  if (Array.isArray(records)) {
    // detect auto vs manual
    const all = records;
    manualList = all.filter(r => !r.isAutoMelding && !(String(r.meldingType||'').toLowerCase()==='automatisch'));
    autoCount = all.length - manualList.length;

    manualList.forEach(d => {
      const hasLink = Boolean(d.robotDocumentId || d.kNummer || d.robotId || (d.selectedRobot && (d.selectedRobot.id || d.selectedRobot.value)) );
      if (hasLink) linked.push(d.id || d.docId || ''); else manualReview.push(d.id || d.docId || '');
    });
  } else if (Array.isArray(manualDocs)) {
    manualList = manualDocs;
    linked = Array.isArray(linkedIds) ? linkedIds.slice() : [];
    manualReview = Array.isArray(manualReviewIds) ? manualReviewIds.slice() : [];
  }

  const manualMeldingenCount = manualList.length;
  const analyticsLinkedCount = linked.length;
  const analyticsManualReviewCount = manualReview.length;
  const combinedMeldingenCount = manualMeldingenCount + autoCount;

  const robotsWithResults = new Set();
  const manualById = new Map((manualList || []).map(d => [d.id || d.docId || '', d]));
  linked.forEach(id => {
    const doc = manualById.get(id) || {};
    const canonical = getCanonicalRobotName(doc, 'Onbekend', { preferLookup: true }) || (doc.robotCanonical || '');
    if (canonical) robotsWithResults.add(canonical);
  });

  const totalRobotsCount = Array.isArray(robots) ? robots.length : 0;
  const activeRobotsCount = Array.isArray(robots) ? robots.filter(isAnalyticsActiveRobot).length : 0;
  const robotsWithResultsCount = robotsWithResults.size;
  const robotsWithoutResultsCount = Math.max(0, totalRobotsCount - robotsWithResultsCount);

  return {
    manualMeldingenCount,
    analyticsLinkedCount,
    analyticsManualReviewCount,
    autoMeldingenCount: autoCount,
    combinedMeldingenCount,
    visibleAnalyticsCount: analyticsLinkedCount,
    robotsWithResultsCount,
    totalRobotsCount,
    activeRobotsCount,
    robotsWithoutResultsCount,
    recordsWithoutRobotMappingCount: analyticsManualReviewCount,
    manualReviewIds: manualReview,
  };
}

export function extractManualReviewDetails(doc = {}) {
  return {
    id: doc.id || doc.docId || null,
    timestamp: doc.timestamp || doc.date || null,
    rawRobotReference: doc.robot || doc.naam || doc.name || null,
    robotNumber: doc.kNummer || doc.robotNummer || null,
    robotName: doc.naam || doc.name || null,
    gemeente: doc.gemeente || null,
    project: doc.projectNummer || doc.projectCode || null,
    kNummer: doc.kNummer || null,
    existingReviewReason: doc._reviewReason || doc.reviewReason || null,
    candidateRobots: doc._candidates || [],
  };
}

export function computeMesjes({ records = [] } = {}){
  let totalMesjesExplicit = 0;
  (records || []).forEach(r => {
    if (r.mesjesExplicit != null && Number.isFinite(Number(r.mesjesExplicit))) totalMesjesExplicit += Number(r.mesjesExplicit);
    else if (r.mesjes != null && Number.isFinite(Number(r.mesjes))) totalMesjesExplicit += Number(r.mesjes);
  });
  return { totalMesjesExplicit };
}

export function normalizeTechnician(raw = {}){
  const res = techNormalizer.normalizeTechnician(raw);
  return res && res.canonical ? res.canonical : 'Niet geregistreerd';
}

export function resolveMesjesUsage(doc = {}) {
  if (doc == null) return { mesjes: 0, source: 'none', reliable: false };
  const m = doc.mesjes;
  if (m == null) return { mesjes: 0, source: 'none', reliable: false };
  const val = Number(m);
  if (!Number.isFinite(val)) return { mesjes: 0, source: 'invalid', reliable: false };
  return { mesjes: val, source: 'explicit', reliable: true };
}

export default { computeAnalyticsCounts, extractManualReviewDetails, resolveMesjesUsage, computeMesjes, getRobotIdentityKey, normalizeTechnician };
